import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { getSession, clearSession } from "@/lib/api";

const links = [
  { to: "/", label: "Home" },
  { to: "/philosophy", label: "Philosophy" },
  { to: "/preview", label: "Recipes" },
  { to: "/pantry", label: "Pantry" },
  { to: "/shop", label: "Shop" },
];

export default function Nav() {
  const token = getSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Lock body scroll + escape-to-close while drawer is open
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleSignOut = () => { clearSession(); setOpen(false); navigate("/"); };

  return (
    <header className="sticky top-0 z-40 bg-mizizi-forest/95 backdrop-blur-xl border-b border-mizizi-gold/30">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 py-3.5 md:py-5 flex items-center justify-between gap-3">
        <Link to="/" data-testid="nav-logo" className="flex items-baseline gap-2 min-w-0">
          <span className="font-serif text-xl sm:text-2xl md:text-3xl text-mizizi-cream tracking-tight">MIZIZI</span>
          <span className="hidden md:inline overline text-mizizi-gold">Rooted at the Table</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          {links.map(l => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `overline transition-colors duration-200 ${isActive ? "text-mizizi-gold" : "text-mizizi-cream/70 hover:text-mizizi-cream"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {token ? (
            <>
              <Link to="/read" data-testid="nav-read" className="pill pill-outline pill-inline-mobile text-mizizi-gold border-mizizi-gold">Reader</Link>
              <button
                data-testid="nav-signout"
                onClick={handleSignOut}
                className="hidden md:inline overline text-mizizi-cream/60 hover:text-mizizi-cream"
              >
                Sign out
              </button>
            </>
          ) : (
            <Link to="/login" data-testid="nav-signin" className="pill pill-outline pill-inline-mobile text-mizizi-gold border-mizizi-gold">Sign in</Link>
          )}
          <button
            type="button"
            aria-label="Open menu"
            aria-expanded={open}
            aria-controls="mobile-nav-drawer"
            data-testid="nav-hamburger"
            onClick={() => setOpen(true)}
            className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-mizizi-gold/60 text-mizizi-gold hover:bg-mizizi-gold/10 transition-colors"
          >
            <Menu size={20} />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          className="md:hidden fixed inset-0 z-50 h-screen w-screen"
          role="dialog"
          aria-modal="true"
          data-testid="mobile-drawer"
        >
          <button
            type="button"
            aria-label="Close menu"
            data-testid="mobile-drawer-backdrop"
            onClick={() => setOpen(false)}
            className="fixed inset-0 bg-mizizi-charcoal/70 backdrop-blur-sm"
          />
          <aside
            id="mobile-nav-drawer"
            className="fixed top-0 right-0 h-screen w-[85%] max-w-sm bg-mizizi-forest text-mizizi-cream border-l border-mizizi-gold/40 shadow-2xl flex flex-col animate-fade-up"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-mizizi-gold/30">
              <span className="font-serif text-2xl tracking-tight">MIZIZI</span>
              <button
                type="button"
                aria-label="Close menu"
                data-testid="mobile-drawer-close"
                onClick={() => setOpen(false)}
                className="inline-flex items-center justify-center w-11 h-11 rounded-full text-mizizi-cream/80 hover:text-mizizi-cream hover:bg-mizizi-cream/10 transition-colors"
              >
                <X size={22} />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-6 py-8">
              <ul className="space-y-1">
                {links.map((l) => (
                  <li key={l.to}>
                    <NavLink
                      to={l.to}
                      end={l.to === "/"}
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                      className={({ isActive }) =>
                        `block font-serif text-3xl py-3 border-b border-mizizi-gold/15 transition-colors ${isActive ? "text-mizizi-gold" : "text-mizizi-cream hover:text-mizizi-gold"}`
                      }
                    >
                      {l.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </nav>
            <div className="px-6 pb-8 pt-4 border-t border-mizizi-gold/30 space-y-3">
              {token ? (
                <>
                  <Link
                    to="/read"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-read"
                    className="pill pill-primary"
                  >
                    Open the Reader
                  </Link>
                  <button
                    type="button"
                    onClick={handleSignOut}
                    data-testid="mobile-nav-signout"
                    className="pill pill-outline"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-signin"
                  className="pill pill-primary"
                >
                  Sign In
                </Link>
              )}
              <p className="overline text-mizizi-gold/70 text-center pt-2">Rooted at the Table</p>
            </div>
          </aside>
        </div>
      )}
    </header>
  );
}
